/**
 * Shared cumulative-delta scroll-direction resolver, extracted from
 * `ConversationTranscript`'s scroll handling so `NavBar`'s window-scroll
 * hide/show and the transcript's container-scroll hide/show run the exact
 * same logic instead of two independently-drifting inline copies.
 *
 * Callers own their own position source (`window.scrollY` vs `el.scrollTop`)
 * and their own `ScrollDirectionState` — this function only decides, from a
 * new position + the last-fired baseline, whether enough cumulative movement
 * has happened to report a direction.
 */
export interface ScrollDirectionState {
  /** The position (px) at which 'up'/'down' was last reported. */
  lastFiredPos: number;
}

export function createScrollDirectionState(): ScrollDirectionState {
  return { lastFiredPos: 0 };
}

/**
 * Returns 'up' | 'down' when cumulative movement since the last report
 * crosses `threshold`, or `null` when it hasn't (the caller should treat
 * `null` as "no-op, don't change anything"). Fires 'up' unconditionally once
 * `pos` reaches the top (`pos <= 0`) regardless of the last-fired baseline —
 * there's nothing further up to justify staying hidden. Mutates
 * `state.lastFiredPos` only when it decides to fire, so several small moves
 * in the same direction (e.g. smooth-scroll animation frames) accumulate
 * toward the threshold instead of each resetting against the immediately
 * prior position.
 */
export function resolveScrollDirection(
  pos: number,
  state: ScrollDirectionState,
  threshold: number,
): 'up' | 'down' | null {
  if (pos <= 0) {
    state.lastFiredPos = 0;
    return 'up';
  }
  const delta = pos - state.lastFiredPos;
  if (Math.abs(delta) < threshold) return null;
  state.lastFiredPos = pos;
  return delta > 0 ? 'down' : 'up';
}
